import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, Check, X, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { graduierungAPI } from '@/lib/api';

/* Octiv-Altbestand (28.09.2026): Anwesenheiten nachtragen und Gürtel einstufen,
   damit ein Mitglied beim Wechsel in die App seinen Stand und das nächste Ziel
   sieht. Zwei Schritte:
   1. Octiv-Bericht „member-attendance-report“ hochladen -> Vorschau -> übernehmen.
      Es wird nur die Lücke seit dem Erstimport (11.09.) ergänzt, nie doppelt.
   2. Gürtel-Vorschläge aus der Trainingszahl prüfen: übernehmen, andere Stufe
      wählen oder verwerfen. Übernahme ist eine Einstufung ohne Push. */
const STATUS_TEXT = { exakt: 'zugeordnet', aehnlich: 'zugeordnet (ähnlich)', manuell: 'zugeordnet (fest)', unsicher: 'unsicher',
  mehrdeutig: 'mehrdeutig', nicht_gefunden: 'nicht gefunden', uebersprungen: 'übersprungen', keine_buchungen: 'keine Buchungen', fehler: 'Fehler' };

export default function AltbestandPanel({ onGeaendert }) {
  const datei = useRef(null);
  const [gewaehlt, setGewaehlt] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [busy, setBusy] = useState(false);
  const [vorschlaege, setVorschlaege] = useState([]);
  const [stufen, setStufen] = useState([]);
  const [wahl, setWahl] = useState({});
  const [zeigeAlle, setZeigeAlle] = useState(false);

  const ladeVorschlaege = useCallback(async () => {
    try {
      const r = await graduierungAPI.vorschlaege();
      setVorschlaege(r.data.vorschlaege || []); setStufen(r.data.stufen || []);
      // Angabe des Mitglieds aus der App geht als Vorauswahl vor (Trainer kann ändern)
      setWahl(Object.fromEntries((r.data.vorschlaege || []).map(v => [v.id, v.angabe || v.vorschlag])));
    } catch { /* Panel bleibt leer */ }
  }, []);
  useEffect(() => { ladeVorschlaege(); }, [ladeVorschlaege]);

  const pruefen = async (f) => {
    setGewaehlt(f); setVorschau(null); setBusy(true);
    try { setVorschau((await graduierungAPI.octivNachtrag(f, false)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Datei konnte nicht gelesen werden'); setGewaehlt(null); }
    finally { setBusy(false); }
  };
  const uebernehmenNachtrag = async () => {
    if (!gewaehlt) return;
    setBusy(true);
    try {
      const r = (await graduierungAPI.octivNachtrag(gewaehlt, true)).data;
      toast.success(`${r.geschrieben} Anwesenheiten nachgetragen · ${r.vorschlaege?.offen ?? 0} Gürtel-Vorschläge offen`);
      setVorschau(null); setGewaehlt(null); ladeVorschlaege(); onGeaendert?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übernahme fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const neuBerechnen = async () => {
    setBusy(true);
    try { const r = (await graduierungAPI.vorschlaegeBerechnen()).data; toast.success(`${r.offen} Vorschläge offen (${r.neu} neu)`); ladeVorschlaege(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Berechnung fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const entscheiden = async (v, art) => {
    try {
      if (art === 'ok') {
        const stufe = wahl[v.id] && wahl[v.id] !== v.vorschlag ? wahl[v.id] : undefined;
        const r = (await graduierungAPI.vorschlagUebernehmen(v.id, stufe)).data;
        toast.success(`${v.name}: ${r.label} eingetragen`);
      } else {
        await graduierungAPI.vorschlagVerwerfen(v.id); toast.success(`${v.name}: Vorschlag verworfen`);
      }
      setVorschlaege(l => l.filter(x => x.id !== v.id)); onGeaendert?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
  };
  const alleUebernehmen = async () => {
    if (!window.confirm(`${vorschlaege.length} Gürtel so eintragen, wie sie in der Liste stehen? Das Mitglied bekommt keine Benachrichtigung.`)) return;
    setBusy(true); let n = 0;
    for (const v of vorschlaege) {
      try { const stufe = wahl[v.id] && wahl[v.id] !== v.vorschlag ? wahl[v.id] : undefined; await graduierungAPI.vorschlagUebernehmen(v.id, stufe); n += 1; } catch { /* weiter */ }
    }
    toast.success(`${n} Gürtel eingetragen`); setBusy(false); ladeVorschlaege(); onGeaendert?.();
  };

  const offeneZuordnung = vorschau?.offen || [];
  const sichtbar = zeigeAlle ? vorschlaege : vorschlaege.slice(0, 15);

  return (
    <Card className="border-border/60" data-testid="altbestand-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold">Octiv-Stand übernehmen</p>
            <p className="text-xs text-muted-foreground max-w-2xl">Octiv-Bericht „member-attendance-report“ (CSV) hochladen. Ergänzt wird nur, was seit dem Import am 11.09. dazugekommen ist; eigene CRM-Check-ins seitdem werden abgezogen, damit nichts doppelt zählt.</p>
          </div>
          <div className="flex gap-2">
            <input ref={datei} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) pruefen(f); e.target.value = ''; }} data-testid="altbestand-datei" />
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => datei.current?.click()} disabled={busy}>{busy && !vorschau ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Bericht hochladen</Button>
          </div>
        </div>

        {vorschau && (
          <div className="rounded-lg border border-border/60 p-3 space-y-2 text-sm" data-testid="altbestand-vorschau">
            <p><b>{vorschau.zugeordnet}</b> Mitglieder zugeordnet · <b>{vorschau.luecke_gesamt}</b> Anwesenheiten fehlen im CRM und werden nachgetragen.</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(vorschau.statistik || {}).map(([k, n]) => <Badge key={k} variant="outline" className="text-[10px]">{STATUS_TEXT[k] || k}: {n}</Badge>)}
            </div>
            {offeneZuordnung.length > 0 && (
              <div className="text-xs rounded-md bg-amber-500/10 border border-amber-500/30 p-2">
                <p className="font-semibold mb-1">Nicht zugeordnet, bleibt unberücksichtigt ({offeneZuordnung.length}):</p>
                <ul className="space-y-0.5">{offeneZuordnung.map(o => <li key={o.octiv_name}>{o.octiv_name} · {o.buchungen} Buchungen · {STATUS_TEXT[o.status]}{o.hinweis ? ` · ${o.hinweis}` : ''}</li>)}</ul>
              </div>
            )}
            <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Nachträge je Mitglied</summary>
              <div className="max-h-60 overflow-y-auto mt-1">
                {vorschau.zeilen.filter(z => z.luecke > 0).map(z => <div key={z.octiv_name} className="flex justify-between border-b border-border/30 py-0.5"><span>{z.crm_name || z.octiv_name}</span><span className="tabular-nums">+{z.luecke} (Octiv {z.buchungen}, im CRM {z.bereits}{z.eigene_seit_stichtag ? ` + ${z.eigene_seit_stichtag} eigene` : ''})</span></div>)}
              </div>
            </details>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setVorschau(null); setGewaehlt(null); }}>Abbrechen</Button>
              <Button size="sm" onClick={uebernehmenNachtrag} disabled={busy} data-testid="altbestand-uebernehmen">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Übernehmen (${vorschau.luecke_gesamt})`}</Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold">Gürtel-Vorschläge {vorschlaege.length > 0 && <Badge variant="secondary" className="ml-1">{vorschlaege.length} offen</Badge>}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="gap-1.5 h-8" onClick={neuBerechnen} disabled={busy}><RefreshCw className="w-3.5 h-3.5" /> Neu berechnen</Button>
              {vorschlaege.length > 0 && <Button size="sm" className="h-8" onClick={alleUebernehmen} disabled={busy} data-testid="vorschlaege-alle">Alle übernehmen</Button>}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Hat das Mitglied beim ersten App-Login seinen Gürtel angegeben, ist diese Angabe vorausgewählt. Sonst gilt der Vorschlag aus der Trainingszahl seit 01.01.2025. Wer länger dabei ist oder schon geprüft wurde, trägt oft einen höheren Gürtel: dann in der Liste die richtige Stufe wählen. Übernehmen trägt den Gürtel ohne Benachrichtigung ein; ab dann zeigen CRM und App das nächste Prüfungsziel.</p>
          {vorschlaege.length === 0 ? <p className="text-xs text-muted-foreground">Keine offenen Vorschläge.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border/60">{['Mitglied', 'Trainings', 'Ist', 'Angabe Mitglied', 'Gürtel eintragen', 'Danach nächstes Ziel', ''].map(h => <th key={h} className="text-left p-2 text-xs font-semibold text-muted-foreground">{h}</th>)}</tr></thead>
                <tbody>
                  {sichtbar.map(v => {
                    const gw = stufen.find(s => s.key === (wahl[v.id] || v.vorschlag));
                    const idx = stufen.findIndex(s => s.key === gw?.key);
                    const ziel = idx >= 0 ? stufen[idx + 1] : null;
                    return (
                      <tr key={v.id} className="border-b border-border/30" data-testid={`vorschlag-${v.member_id}`}>
                        <td className="p-2 font-medium">{v.name}</td>
                        <td className="p-2 tabular-nums">{v.trainings}</td>
                        <td className="p-2 text-xs text-muted-foreground">{stufen.find(s => s.key === v.ist_belt)?.label || '–'}</td>
                        <td className="p-2 text-xs">{v.angabe ? <span className="inline-flex items-center gap-1" title={`aus der App${v.angabe_von ? ' · ' + v.angabe_von : ''}`}><span className="w-2.5 h-2.5 rounded-full border border-border" style={{ background: v.angabe_farbe || '#eee' }} />{v.angabe_label}{v.angabe_quelle === 'app_datenabgleich_eltern' ? ' (Eltern)' : ''}</span> : <span className="text-muted-foreground">–</span>}</td>
                        <td className="p-2">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-full border border-border" style={{ background: gw?.color || '#eee' }} />
                            <select value={wahl[v.id] || v.vorschlag} onChange={e => setWahl(w => ({ ...w, [v.id]: e.target.value }))} className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                              {stufen.map(s => <option key={s.key} value={s.key}>{s.label}{s.key === v.angabe ? ' (Angabe)' : s.key === v.vorschlag ? ' (Vorschlag)' : ''}</option>)}
                            </select>
                          </span>
                        </td>
                        <td className="p-2 text-xs">{ziel ? `${ziel.label} · noch ${Math.max(0, (ziel.trainings_required || 0) - v.trainings)} Trainings` : 'höchste Stufe'}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <Button size="sm" className="h-7 text-xs mr-1" onClick={() => entscheiden(v, 'ok')}><Check className="w-3 h-3 mr-0.5" /> Eintragen</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => entscheiden(v, 'weg')} title="Vorschlag verwerfen"><X className="w-3 h-3" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {vorschlaege.length > 15 && <button type="button" className="text-xs text-primary hover:underline mt-1" onClick={() => setZeigeAlle(a => !a)}>{zeigeAlle ? 'weniger zeigen' : `alle ${vorschlaege.length} zeigen`}</button>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
